/* agb-collection-filter.js -- panel-managed filter row on collection pages.
 *
 * ONE file, byte-identical on ROW / EU / IL. Store differences come from the
 * theme globals at runtime, never from forked copies:
 *   window.AGB_COLLECTION_FILTER_CONFIG_URL  panel base + '/api/product-tagger-config'
 *   window.AGB_COLLECTION_HANDLE             {{ collection.handle }} (collection template only)
 *   window.AGB_STORE_REGION                  'US-ROW' | 'International-EU' | 'IL' (X-Store-Region)
 * Kill switch: window.AGB_COLLECTION_FILTER_OFF === true,
 *              sessionStorage AGB_COLLECTION_FILTER_OFF = '1', or #cfoff in the URL.
 *
 * What it does. The panel's Product Tagger holds filters (key + labels per
 * locale), each a CHECKBOX (one checkbox here) or a DROPDOWN (one button that
 * opens a list of options, each option a checkbox). Products belong to a
 * "member key": a checkbox filter key ('pouches') or '<dropdown>.<option>'
 * ('caliber.5-56'). Membership comes from the envelope's resolved.members
 * (the backend's evaluation of the filter rules + hand-picked products) and
 * falls back, key by key, to config.members (v1 behaviour). On a collection
 * that has an enabled row, the row shows the row's filters in its order.
 * Semantics:
 *   - all checkbox filters of the row are ONE group, OR inside it;
 *   - each dropdown is its own group, OR among its checked options;
 *   - a product shows when it passes EVERY group that has a selection;
 *   - nothing selected anywhere = the page exactly as the theme rendered it;
 *   - a checkbox / option with no product in this collection is not shown,
 *     and a dropdown left with no option is not shown.
 * The dropdown's option list (a popover) is appended to <body> with fixed
 * positioning, so the mobile horizontal chip strip never clips it.
 * The whole collection is filtered, not just the current page: when the grid
 * paginates, the other pages are loaded through the Section Rendering API and
 * kept DETACHED (counts + zero-match removal read them from memory). A unit of
 * another page enters product-list (class agb-cf-extra) only while it matches
 * the selection, and leaves it again when it stops matching / on Clear. So
 * while nothing is selected the grid holds exactly the theme's own cards (no
 * hidden extra is ever "rendered" for analytics, card positions unchanged).
 * Nothing native is ever removed; hiding a native card is a class
 * (agb-cf-hide).
 *
 * Fail open: no config, a failed or slow (> 5 s) fetch, no row for this
 * collection, or any unexpected error -> the page is left exactly as it is.
 *
 * Coexistence:
 *   - Grid unit = the DIRECT child of product-list (ROW: div.product wrapper
 *     with data-product-handle; EU / IL: product-card[handle]).
 *   - The old per-template reorderers (ROW custom-liquid) wipe product-list
 *     innerHTML on DOMContentLoaded; collection-reorder.js moves units;
 *     Prestige facet:update swaps the whole main-collection section. A
 *     MutationObserver on the section re-applies the filter after each of
 *     them (re-finds product-list, re-attaches dropped matching extras).
 *   - We start after the DOMContentLoaded EVENT + one macrotask, i.e. after
 *     those (a deferred script runs while readyState is already
 *     'interactive', before DOMContentLoaded, so readyState alone is not
 *     enough).
 *   - A pointerdown on the row bubbles to window, which collection-reorder.js
 *     counts as engagement (it then stops reordering).
 *
 * URL state: ?category=pouches,caliber.5-56 (member keys) via
 * history.replaceState (other params + hash kept).
 * Analytics (CAE, via window.CAEDev.emit):
 *   collection_filter_shown {collection:{handle,id}, filters:[keys]} once per page
 *     view, when the row is on the page (the denominator for usage rates);
 *   collection_filter {collection:{handle,id}, selected:[member keys], shown:n},
 *     debounced 800 ms after a change.
 * `collection` MUST be an object: the ingest reads collection.handle into the
 * collection_handle column, and a plain string made it drop the whole event.
 * Debug: logs on preview themes (or with ?agbdebug), silenced by
 * window.__AGB_consoleCategories.collectionFilter === false.
 * Devtools: window.__agbCollectionFilter.
 */
(function () {
  'use strict';

  var SRC = 'agb-collection-filter.js';
  var LOG_PREFIX = '[agb-collection-filter]';
  var FETCH_TIMEOUT_MS = 5000;
  var PAGE_TIMEOUT_MS = 10000;
  var MAX_PAGES = 12;
  var CAE_DEBOUNCE_MS = 800;
  var PARAM = 'category';
  var CLS_HIDE = 'agb-cf-hide';
  var CLS_EXTRA = 'agb-cf-extra';
  var CLS_LINKROW = 'agb-cf-linkrow-hidden';
  var OURS = '__agbCfExtra';   // expando on our own (other-page) unit nodes
  var POP_GAP = 6;             // px between a dropdown button and its popover
  var POP_GUTTER = 16;         // px kept free at the viewport edges
  var CLAMP_SCROLL_MS = 350;   // a strip scroll this soon after our own repaint = the browser clamping scrollLeft

  try {
    if (window.__agbCollectionFilterLoaded) return;
    window.__agbCollectionFilterLoaded = true;
  } catch (e) { return; }

  // ===== Gates ===============================================================

  function killed() {
    try { if (window.AGB_COLLECTION_FILTER_OFF === true) return true; } catch (e) {}
    try { if (window.sessionStorage.getItem('AGB_COLLECTION_FILTER_OFF') === '1') return true; } catch (e) {}
    try { if (/cfoff/i.test(String(window.location.hash || ''))) return true; } catch (e) {}
    return false;
  }

  var HANDLE = '';
  try {
    HANDLE = typeof window.AGB_COLLECTION_HANDLE === 'string'
      ? window.AGB_COLLECTION_HANDLE.replace(/^\s+|\s+$/g, '').toLowerCase() : '';
  } catch (e) { HANDLE = ''; }
  var CONFIG_URL = '';
  try {
    CONFIG_URL = typeof window.AGB_COLLECTION_FILTER_CONFIG_URL === 'string'
      ? window.AGB_COLLECTION_FILTER_CONFIG_URL : '';
  } catch (e) { CONFIG_URL = ''; }

  if (!HANDLE || !CONFIG_URL || killed()) return;

  // ===== Env, logging, error reporting =====================================

  // Live vs Preview storefront detection (same as agb-offers.js).
  function _agbDetectStorefrontEnv() {
    try {
      var urlForce = new URLSearchParams(location.search).get('storefront_env');
      if (urlForce === 'preview' || urlForce === 'live') return urlForce;
    } catch (_) {}
    try {
      var stored = localStorage.getItem('AGB_StorefrontEnv');
      if (stored === 'preview' || stored === 'live') return stored;
    } catch (_) {}
    var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    if (role === 'main') return 'live';
    if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
    var host = (location && location.hostname) || '';
    if (host.indexOf('.shopifypreview.com', host.length - 20) !== -1) return 'preview';
    if (host.indexOf('.myshopify.com', host.length - 14) !== -1) return 'preview';
    if (host === '127.0.0.1' || host === 'localhost') return 'preview';
    try {
      if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
    } catch (_) {}
    return 'preview';
  }

  var ENV = _agbDetectStorefrontEnv();
  var LS_CONFIG_KEY = 'AGB_CollFilter_' + ENV;
  var LS_CONFIG_ETAG = 'AGB_CollFilterEtag_' + ENV;
  var DEBUG_URL = false;
  try { DEBUG_URL = /[?&]agbdebug(=|&|$)/.test(location.search || ''); } catch (e) {}

  function _dbg() {
    try {
      if (ENV !== 'preview' && !DEBUG_URL) return;
      var cats = window.__AGB_consoleCategories;
      if (!DEBUG_URL && cats && typeof cats === 'object' && cats.collectionFilter === false) return;
      console.log.apply(console, [LOG_PREFIX].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function ereReport(err, where) {
    try {
      _dbg('error in', where, err);
      if (!window.ERE || typeof window.ERE.report !== 'function') return;
      window.ERE.report(err, {
        source: SRC,
        severity: 'warn',
        context: { where: where || null, collection: HANDLE }
      });
    } catch (e) {}
  }

  // Every entry point goes through this: never throws into the page.
  function guard(where, fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (err) { ereReport(err, where); }
    };
  }

  // ===== Language + strings =================================================

  function pageLocale() {
    var l = '';
    try { l = (window.Shopify && window.Shopify.locale) || ''; } catch (e) {}
    if (!l) { try { l = (document.documentElement && document.documentElement.lang) || ''; } catch (e) {} }
    l = String(l || 'en').toLowerCase().replace('_', '-');
    return l || 'en';
  }
  var LOCALE = pageLocale();
  var LANG = LOCALE.split('-')[0] || 'en';

  // ddOne / ddMany: the dropdown button while options are checked
  // ('Caliber: 5.56' / 'Caliber (2)').
  var STRINGS = {
    en: { legend: 'Filter products', clear: 'Clear', showing: 'Showing {n} products',
          showing1: 'Showing 1 product', none: 'No products match',
          ddOne: '{label}: {option}', ddMany: '{label} ({n})' },
    de: { legend: 'Produkte filtern', clear: 'Zurücksetzen', showing: '{n} Produkte werden angezeigt',
          showing1: '1 Produkt wird angezeigt', none: 'Keine passenden Produkte' },
    fr: { legend: 'Filtrer les produits', clear: 'Effacer', showing: '{n} produits affichés',
          showing1: '1 produit affiché', none: 'Aucun produit ne correspond',
          ddOne: '{label} : {option}' },
    es: { legend: 'Filtrar productos', clear: 'Borrar', showing: 'Mostrando {n} productos',
          showing1: 'Mostrando 1 producto', none: 'Ningún producto coincide' },
    he: { legend: 'סינון מוצרים',
          clear: 'ניקוי',
          showing: 'מוצגים {n} מוצרים',
          showing1: 'מוצג מוצר אחד',
          none: 'אין מוצרים מתאימים' }
  };
  function str(key, n) {
    var t = (STRINGS[LANG] && STRINGS[LANG][key]) || STRINGS.en[key] || '';
    return n == null ? t : t.replace('{n}', String(n));
  }
  function fill(tpl, vals) {
    return String(tpl).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vals, k) ? String(vals[k]) : m;
    });
  }

  function humanize(key) {
    var s = String(key || '').replace(/-+/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  // Filters and options share the label shape {locale: text}.
  function labelFor(tag) {
    var labels = (tag && tag.labels && typeof tag.labels === 'object') ? tag.labels : {};
    function usable(v) { return typeof v === 'string' && !!v.replace(/\s+/g, ''); }
    if (usable(labels[LOCALE])) return labels[LOCALE];
    if (usable(labels[LANG])) return labels[LANG];
    // A label stored under a regional key (e.g. 'de-de') when the page is 'de'.
    for (var k in labels) {
      if (!Object.prototype.hasOwnProperty.call(labels, k)) continue;
      if (String(k).split('-')[0] === LANG && usable(labels[k])) return labels[k];
    }
    if (usable(labels.en)) return labels.en;
    return humanize(tag && tag.key);
  }

  // ===== Small DOM helpers (ES5, no closest/forEach on NodeList) ============

  function isEl(n) { return !!n && n.nodeType === 1; }
  function hasCls(el, c) { return !!(el && el.classList && el.classList.contains(c)); }
  function setCls(el, c, on) {
    if (!el || !el.classList) return;
    if (on) { if (!el.classList.contains(c)) el.classList.add(c); }
    else if (el.classList.contains(c)) el.classList.remove(c);
  }
  function closestMatch(el, test) {
    while (el && el.nodeType === 1) { if (test(el)) return el; el = el.parentNode; }
    return null;
  }
  function inDoc(node) {
    try { return !!node && document.documentElement.contains(node); } catch (e) { return false; }
  }
  function elementChildren(el) {
    var out = [];
    var c = el ? el.children : null;
    if (!c) return out;
    for (var i = 0; i < c.length; i++) out.push(c[i]);
    return out;
  }
  function make(tag, cls, text) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  }
  function detach(n) {
    try { if (n && n.parentNode) n.parentNode.removeChild(n); } catch (e) {}
  }
  function keyName(e) {
    var k = (e && e.key) || '';
    if (k === 'Esc') return 'Escape';
    if (k === 'Down') return 'ArrowDown';
    if (!k && e) {
      if (e.keyCode === 27) return 'Escape';
      if (e.keyCode === 9) return 'Tab';
      if (e.keyCode === 40) return 'ArrowDown';
    }
    return k;
  }
  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) {} }
  }
  function activeEl() {
    try { return document.activeElement || null; } catch (e) { return null; }
  }

  // ===== Config ==============================================================

  var HANDLE_RE = /^[^\s\/?#<>"']{1,255}$/;
  var KEY_RE = /^[^\s.]{1,64}$/;   // filter / option keys never contain '.'

  function handleList(arr) {
    var list = [];
    if (!Array.isArray(arr)) return list;
    for (var j = 0; j < arr.length; j++) {
      var h = arr[j];
      if (typeof h === 'string' && HANDLE_RE.test(h)) list.push(h.toLowerCase());
    }
    return list;
  }
  function labelsOf(x) {
    return (x && x.labels && typeof x.labels === 'object' && !Array.isArray(x.labels)) ? x.labels : {};
  }

  // Defensive normalisation: the backend validates, we only refuse what would
  // break the row (never throw on a surprising shape). Accepts the public
  // envelope {config, resolved?} or a cached normalised copy.
  function normalizeConfig(body) {
    if (!body || typeof body !== 'object') return null;
    var c = (body.config && typeof body.config === 'object') ? body.config : body;
    if (!c || typeof c !== 'object') return null;
    var out = { enabled: c.enabled === true, tags: [], members: {}, collectionFilters: [], membersFrom: 'config' };
    var i, j, k;
    var tags = Array.isArray(c.tags) ? c.tags : [];
    var seen = {};
    for (i = 0; i < tags.length; i++) {
      var t = tags[i];
      if (!t || typeof t.key !== 'string' || !KEY_RE.test(t.key) || seen[t.key]) continue;
      var type = t.type == null || t.type === 'checkbox' ? 'checkbox' : t.type === 'dropdown' ? 'dropdown' : '';
      if (!type) continue;   // a filter kind this script does not know: skip it
      seen[t.key] = true;
      var tag = { key: t.key, labels: labelsOf(t), type: type, options: [] };
      if (type === 'dropdown') {
        var opts = Array.isArray(t.options) ? t.options : [];
        var oseen = {};
        for (j = 0; j < opts.length; j++) {
          var o = opts[j];
          if (!o || typeof o.key !== 'string' || !KEY_RE.test(o.key) || oseen[o.key]) continue;
          oseen[o.key] = true;
          tag.options.push({ key: o.key, labels: labelsOf(o) });
        }
      }
      out.tags.push(tag);
    }
    var mem = (c.members && typeof c.members === 'object') ? c.members : {};
    for (k in mem) {
      if (!Object.prototype.hasOwnProperty.call(mem, k) || !Array.isArray(mem[k])) continue;
      out.members[k] = handleList(mem[k]);
    }
    // The backend's rule results (envelope field, NOT inside config). Each key
    // it resolved replaces the hand-picked list; a key it did not resolve
    // keeps config.members (v1 behaviour).
    var res = (body.config && body.resolved && typeof body.resolved === 'object') ? body.resolved : null;
    if (res && res.members && typeof res.members === 'object' && !Array.isArray(res.members)) {
      for (k in res.members) {
        if (!Object.prototype.hasOwnProperty.call(res.members, k) || !Array.isArray(res.members[k])) continue;
        out.members[k] = handleList(res.members[k]);
      }
      out.membersFrom = 'resolved';
      if (typeof res.resolvedAt === 'string') out.resolvedAt = res.resolvedAt;
      if (res.stale === true) out.stale = true;
    } else if (c.membersFrom === 'resolved') {
      out.membersFrom = 'resolved';   // a cached copy
      if (typeof c.resolvedAt === 'string') out.resolvedAt = c.resolvedAt;
      if (c.stale === true) out.stale = true;
    }
    var cf = Array.isArray(c.collectionFilters) ? c.collectionFilters : [];
    for (i = 0; i < cf.length; i++) {
      var r = cf[i];
      if (!r || typeof r.collection !== 'string') continue;
      out.collectionFilters.push({
        collection: r.collection.toLowerCase(),
        enabled: r.enabled !== false,
        tags: Array.isArray(r.tags) ? r.tags.slice(0, 12) : [],
        hideLinkRow: r.hideLinkRow !== false,
        showCounts: r.showCounts === true
      });
    }
    return out;
  }

  function setOf(list) {
    var set = {};
    for (var j = 0; list && j < list.length; j++) set[list[j]] = true;
    return set;
  }

  // The row this page should show, or null.
  //   row.tags    the row's filters in order: checkbox items
  //               {kind:'checkbox', key, label, members, group:''} and
  //               dropdown items {kind:'dropdown', key, label, options:[...]}
  //   row.entries every selectable member key in display order: the checkbox
  //               items themselves and the dropdown options
  //               {kind:'option', key:'<dd>.<opt>', optKey, label, members,
  //               group:'<dd>', parent}
  // A checkbox / option with no member at all is left out up front; one
  // whose members are all outside this collection goes in refreshRow().
  function resolveRow(cfg) {
    if (!cfg || cfg.enabled !== true) return null;
    var row = null;
    for (var i = 0; i < cfg.collectionFilters.length; i++) {
      if (cfg.collectionFilters[i].collection === HANDLE) { row = cfg.collectionFilters[i]; break; }
    }
    if (!row || row.enabled !== true) return null;
    var byKey = {};
    for (i = 0; i < cfg.tags.length; i++) byKey[cfg.tags[i].key] = cfg.tags[i];
    var items = [];
    var entries = [];
    var seen = {};
    for (i = 0; i < row.tags.length; i++) {
      var key = row.tags[i];
      if (typeof key !== 'string' || !byKey[key] || seen[key]) continue;
      seen[key] = true;
      var tag = byKey[key];
      if (tag.type === 'dropdown') {
        var item = { kind: 'dropdown', key: key, label: labelFor(tag), options: [], gone: false };
        for (var j = 0; j < tag.options.length; j++) {
          var o = tag.options[j];
          var mk = key + '.' + o.key;
          // No product at all (no rule result, no hand-picked product): it
          // can never match, so it is never drawn (no flash before the other
          // pages are known).
          if (!cfg.members[mk] || !cfg.members[mk].length) continue;
          item.options.push({
            kind: 'option', key: mk, optKey: o.key, label: labelFor(o),
            members: setOf(cfg.members[mk]), group: key, parent: item, gone: false
          });
        }
        if (!item.options.length) continue;
        items.push(item);
        for (j = 0; j < item.options.length; j++) entries.push(item.options[j]);
      } else {
        if (!cfg.members[key] || !cfg.members[key].length) continue;
        var cb = { kind: 'checkbox', key: key, label: labelFor(tag), members: setOf(cfg.members[key]), group: '', gone: false };
        items.push(cb);
        entries.push(cb);
      }
    }
    if (!items.length) return null;
    return { tags: items, entries: entries, hideLinkRow: row.hideLinkRow, showCounts: row.showCounts };
  }

  function entryFor(key) {
    if (!S.row) return null;
    for (var i = 0; i < S.row.entries.length; i++) if (S.row.entries[i].key === key) return S.row.entries[i];
    return null;
  }
  function liveEntries() {
    var out = [];
    if (!S.row) return out;
    for (var i = 0; i < S.row.entries.length; i++) {
      var e = S.row.entries[i];
      if (!e.gone && !(e.parent && e.parent.gone)) out.push(e);
    }
    return out;
  }

  function readCache() {
    try {
      var raw = localStorage.getItem(LS_CONFIG_KEY);
      if (!raw) return null;
      return normalizeConfig(JSON.parse(raw));
    } catch (e) { return null; }
  }
  // Copy + ETag are one unit (see agb-offers.js): drop the ETag first, store
  // the copy, then the new ETag.
  function writeCache(cfg, etag) {
    try { localStorage.removeItem(LS_CONFIG_ETAG); } catch (e) {}
    var stored = false;
    try { localStorage.setItem(LS_CONFIG_KEY, JSON.stringify(cfg)); stored = true; } catch (e) {}
    if (!stored) { try { localStorage.removeItem(LS_CONFIG_KEY); } catch (e) {} return; }
    if (etag) { try { localStorage.setItem(LS_CONFIG_ETAG, etag); } catch (e) {} }
  }

  function loadConfig(cached, onFresh) {
    var endpoint = ENV === 'preview'
      ? CONFIG_URL + (CONFIG_URL.indexOf('?') === -1 ? '?' : '&') + 'env=preview'
      : CONFIG_URL;
    var headers = { 'Accept': 'application/json' };
    try {
      var etag = cached ? localStorage.getItem(LS_CONFIG_ETAG) : null;
      if (etag) headers['If-None-Match'] = etag;
    } catch (e) {}
    try { var rgn = window.AGB_STORE_REGION || ''; if (rgn) headers['X-Store-Region'] = rgn; } catch (e) {}

    var timedOut = false;
    var to = setTimeout(function () { timedOut = true; _dbg('config timeout - leaving the page as it is'); }, FETCH_TIMEOUT_MS);
    var p;
    try { p = fetch(endpoint, { headers: headers, cache: 'no-store', credentials: 'omit' }); }
    catch (e) { clearTimeout(to); ereReport(e, 'fetch'); return; }
    p.then(function (res) {
      if (res.status === 304) {
        if (!cached) throw new Error('product-tagger-config 304 without a cached copy');
        return { cfg: cached, same: true };
      }
      if (!res.ok) { var he = new Error('product-tagger-config HTTP ' + res.status); he.http = res.status; throw he; }
      return res.json().then(function (body) {
        var c = normalizeConfig(body);
        if (!c) throw new Error('product-tagger-config: unexpected shape');
        var et = null;
        try { et = res.headers.get('ETag'); } catch (e) {}
        writeCache(c, et);
        return { cfg: c, same: false };
      });
    }).then(function (r) {
      clearTimeout(to);
      if (timedOut) { _dbg('config arrived after the timeout - cached for the next view only'); return; }
      onFresh(r.cfg, r.same);
    })['catch'](function (err) {
      clearTimeout(to);
      // 404 = this backend does not serve the tagger (yet): quiet.
      if (err && err.http === 404) { _dbg('config 404 - not served by this backend'); return; }
      ereReport(err, 'config');
    });
  }

  // ===== Grid discovery ======================================================

  function findList() {
    var l = document.querySelector('.shopify-section--main-collection product-list');
    if (l) return l;
    var all = document.querySelectorAll('product-list.product-list');
    for (var i = 0; i < all.length; i++) {
      var sec = sectionOf(all[i]);
      if (sec && /__main$/.test(sec.id || '')) return all[i];
    }
    return all.length ? all[0] : null;
  }
  function sectionOf(el) {
    return closestMatch(el, function (n) {
      return hasCls(n, 'shopify-section') || /^shopify-section-/.test(n.id || '');
    });
  }
  function sectionIdOf(sec) {
    var id = (sec && sec.id) || '';
    return id.indexOf('shopify-section-') === 0 ? id.slice('shopify-section-'.length) : '';
  }

  function unitHandle(unit) {
    if (!isEl(unit)) return '';
    var h = unit.getAttribute('data-product-handle') || unit.getAttribute('handle');
    if (!h) {
      var card = unit.querySelector('product-card[handle]');
      h = card ? card.getAttribute('handle') : '';
    }
    return String(h || '').toLowerCase();
  }
  function isUnit(el) {
    if (!isEl(el)) return false;
    var t = el.tagName;
    return t !== 'SCRIPT' && t !== 'STYLE' && t !== 'TEMPLATE' && t !== 'LINK' && t !== 'NOSCRIPT';
  }
  function unitsOf(list) {
    var out = [];
    var kids = elementChildren(list);
    for (var i = 0; i < kids.length; i++) if (isUnit(kids[i])) out.push(kids[i]);
    return out;
  }
  // Ours = a unit node of another page we loaded. A node carrying the extra
  // class without the expando is a copy of one of ours another script rebuilt.
  function isOurs(u) { return !!(u && u[OURS]); }
  function isExtraNode(u) { return isOurs(u) || hasCls(u, CLS_EXTRA); }
  function nativeUnits(list) {
    var out = [];
    var units = unitsOf(list);
    for (var i = 0; i < units.length; i++) if (!isExtraNode(units[i])) out.push(units[i]);
    return out;
  }

  function pageFromUrl() {
    var m = /[?&]page=(\d+)/.exec(location.search || '');
    var n = m ? parseInt(m[1], 10) : 1;
    return n > 0 ? n : 1;
  }
  function maxPage(sec) {
    var max = 1;
    if (!sec) return max;
    var nav = sec.querySelectorAll('.pagination a[href], nav.pagination a[href]');
    for (var i = 0; i < nav.length; i++) {
      var m = /[?&]page=(\d+)/.exec(nav[i].getAttribute('href') || '');
      if (m) { var n = parseInt(m[1], 10); if (n > max) max = n; }
    }
    var cur = sec.querySelector('.pagination [aria-current="page"]');
    if (cur) { var c = parseInt((cur.textContent || '').replace(/\D+/g, ''), 10); if (c > max) max = c; }
    return max;
  }
  function paginationNavs(sec) {
    return sec ? sec.querySelectorAll('nav.pagination') : [];
  }

  // ===== State ===============================================================

  var S = {
    cfg: null,           // normalised config in use
    cfgJson: '',
    row: null,           // resolveRow() result
    selected: [],        // checked member keys, display order
    list: null,          // product-list
    section: null,       // main-collection section
    sectionId: '',
    current: 1,          // page the theme rendered natively
    pages: 1,
    byPage: {},          // page -> [units] (native page units included, by reference)
    extrasState: 'none', // none | loading | done | failed
    fullKnown: false,
    ui: null,
    openDd: null,        // the dropdown item whose popover is open
    repaintAt: 0,        // when the row last changed width (selection / zero-match removal)
    observer: null,
    observing: null,
    hiddenLinkRows: [],
    ready: false,
    reconcileBurst: 0,
    reconcileWindow: 0,
    caeTimer: 0,
    loadRetried: false
  };

  // ===== Styles ==============================================================

  var INPUT_CSS = '{-webkit-appearance:checkbox;appearance:auto;flex:none;inline-size:16px;block-size:16px;margin:0;padding:0;accent-color:rgb(var(--text-color,28 28 28));cursor:pointer}';
  var FOCUS_CSS = '{outline:2px solid rgb(var(--text-color,28 28 28));outline-offset:2px}';
  var CLEAR_CSS = '{flex:none;min-block-size:36px;margin:0;padding-block:6px;padding-inline:4px;border:0;background:transparent;color:inherit;font:inherit;font-size:var(--text-sm,.875rem);text-decoration:underline;text-underline-offset:3px;cursor:pointer}';
  var CSS = [
    '.' + CLS_HIDE + ',.' + CLS_LINKROW + ',.agb-cf [hidden],.agb-cf-empty [hidden],.agb-cf-pop[hidden],.agb-cf-pop [hidden]{display:none!important}',
    '.agb-cf-vh{position:absolute!important;inline-size:1px!important;block-size:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0 0 0 0)!important;white-space:nowrap!important;border:0!important}',
    '.agb-cf{position:relative;border:0;margin:0;padding:16px 0 0;min-inline-size:0;max-inline-size:100%;text-align:center;font-family:inherit;color:rgb(var(--text-color,28 28 28))}',
    '.agb-cf .agb-cf__bar{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:8px 12px;min-inline-size:0}',
    '.agb-cf .agb-cf__chips{display:flex;flex-wrap:wrap;justify-content:center;gap:8px;min-inline-size:0;margin:0;padding:0}',
    '.agb-cf .agb-cf__chip{display:inline-flex;align-items:center;gap:8px;min-block-size:36px;margin:0;padding-block:6px;padding-inline:12px 14px;border:1px solid rgb(var(--border-color,28 28 28 / .2));border-radius:var(--button-border-radius,0);background:transparent;color:inherit;font-size:var(--text-sm,.875rem);line-height:1.2;white-space:nowrap;cursor:pointer;-webkit-user-select:none;user-select:none;transition:border-color .15s ease,background-color .15s ease}',
    '.agb-cf .agb-cf__chip:hover{border-color:rgb(var(--text-color,28 28 28))}',
    '.agb-cf .agb-cf__chip.is-checked{border-color:rgb(var(--text-color,28 28 28));background-color:rgb(var(--text-color,28 28 28) / .06)}',
    // The chip ring is for keyboard focus only: :focus-within also matched after a
    // mouse click (the click focuses the checkbox), leaving a second frame on the
    // last chip clicked. Where :has() is supported the ring moves from the box to
    // the chip; elsewhere the box keeps its own :focus-visible ring below.
    '.agb-cf label.agb-cf__chip:has(.agb-cf__input:focus-visible)' + FOCUS_CSS,
    '@supports selector(:has(a)){.agb-cf label.agb-cf__chip .agb-cf__input:focus-visible{outline:none}}',
    '.agb-cf .agb-cf__dd{-webkit-appearance:none;appearance:none;font-family:inherit;font-weight:inherit;letter-spacing:inherit;text-transform:none}',
    '.agb-cf .agb-cf__dd:focus-visible' + FOCUS_CSS,
    '.agb-cf .agb-cf__caret{display:inline;font-size:.75em;line-height:1;opacity:.8}',
    '.agb-cf .agb-cf__input,.agb-cf-pop .agb-cf__input' + INPUT_CSS,
    '.agb-cf .agb-cf__input:focus-visible,.agb-cf-pop .agb-cf__input:focus-visible' + FOCUS_CSS,
    '.agb-cf .agb-cf__text,.agb-cf .agb-cf__count,.agb-cf-pop .agb-cf__text,.agb-cf-pop .agb-cf__count{display:inline;margin:0}',
    '.agb-cf .agb-cf__count,.agb-cf-pop .agb-cf__count{opacity:.7}',
    '.agb-cf .agb-cf__clear,.agb-cf-empty .agb-cf__clear,.agb-cf-pop .agb-cf__clear' + CLEAR_CSS,
    '.agb-cf .agb-cf__clear:focus-visible,.agb-cf-empty .agb-cf__clear:focus-visible,.agb-cf-pop .agb-cf__clear:focus-visible' + FOCUS_CSS,
    '.agb-cf-empty{padding-block:32px;padding-inline:16px;text-align:center;color:rgb(var(--text-color,28 28 28))}',
    '.agb-cf-empty p{margin:0 0 8px}',
    '.agb-cf-pop{position:fixed;z-index:50;box-sizing:border-box;min-inline-size:180px;max-inline-size:calc(100vw - ' + (2 * POP_GUTTER) + 'px);max-block-size:calc(100vh - ' + (2 * POP_GUTTER) + 'px);overflow:auto;overscroll-behavior:contain;margin:0;padding:6px;border:1px solid rgb(var(--border-color,28 28 28 / .2));border-radius:var(--button-border-radius,0);background:#fff;color:rgb(var(--text-color,28 28 28));box-shadow:0 5px 30px rgb(0 0 0 / .12);font-size:var(--text-sm,.875rem);line-height:1.2;text-align:start}',
    '.agb-cf-pop .agb-cf-pop__list{display:flex;flex-direction:column;gap:2px;margin:0;padding:0}',
    '.agb-cf-pop .agb-cf-pop__opt{display:flex;align-items:center;gap:10px;min-block-size:40px;margin:0;padding-block:4px;padding-inline:10px;color:inherit;white-space:nowrap;cursor:pointer;-webkit-user-select:none;user-select:none}',
    '.agb-cf-pop .agb-cf-pop__opt:hover,.agb-cf-pop .agb-cf-pop__opt.is-checked{background-color:rgb(var(--text-color,28 28 28) / .06)}',
    '.agb-cf-pop .agb-cf-pop__clear{display:block;margin-block-start:4px;margin-inline-start:6px}',
    '@media (max-width:699px){',
    '.agb-cf .agb-cf__bar{flex-wrap:nowrap;justify-content:flex-start}',
    '.agb-cf .agb-cf__chips{flex:1 1 auto;flex-wrap:nowrap;justify-content:flex-start;overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;scrollbar-width:none;overscroll-behavior-inline:contain;padding-block:4px;scroll-padding-inline:4px}',
    '.agb-cf .agb-cf__chips::-webkit-scrollbar{display:none}',
    '.agb-cf .agb-cf__chip{flex:none;min-block-size:40px}',
    '.agb-cf .agb-cf__chip:first-child{margin-inline-start:auto}',
    '.agb-cf .agb-cf__chip:last-child{margin-inline-end:auto}',
    '.agb-cf .agb-cf__clear{min-block-size:40px}',
    '.agb-cf-pop .agb-cf-pop__opt{min-block-size:44px}',
    '}'
  ].join('\n');

  function injectStyle() {
    if (document.getElementById('agb-cf-style')) return;
    var st = document.createElement('style');
    st.id = 'agb-cf-style';
    st.textContent = CSS;
    (document.head || document.documentElement).appendChild(st);
  }

  // ===== Link row (collection.description) ===================================

  var BLOCK_TAGS = { P: 1, DIV: 1, UL: 1, OL: 1, H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1,
    SECTION: 1, BLOCKQUOTE: 1, TABLE: 1, NAV: 1, ARTICLE: 1, HEADER: 1, FOOTER: 1, FIGURE: 1 };
  var SEPARATORS_ONLY = /^[\s ​-‍⁠﻿·•|,\/\-–—]*$/;

  function textOutsideLinks(node) {
    var out = '';
    var kids = node.childNodes || [];
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.nodeType === 3) out += k.nodeValue || '';
      else if (k.nodeType === 1 && k.tagName !== 'A' && k.tagName !== 'SCRIPT' && k.tagName !== 'STYLE') out += textOutsideLinks(k);
    }
    return out;
  }
  var SEPARATOR_RUN = /[\s ​-‍⁠﻿·•|,\/\-–—]+/g;
  var SENTENCE_PUNCT = /[.!?:;…]/;
  var COLLECTION_HREF = /\/collections\/[^\/?#]/;

  // The text between / around the links, one string per gap, plus how many
  // links point at a collection.
  function collectGaps(node, acc) {
    var kids = node.childNodes || [];
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.nodeType === 3) acc.gaps[acc.gaps.length - 1] += k.nodeValue || '';
      else if (k.nodeType !== 1 || k.tagName === 'SCRIPT' || k.tagName === 'STYLE') continue;
      else if (k.tagName === 'A') {
        if (COLLECTION_HREF.test(k.getAttribute('href') || '')) acc.coll++;
        acc.gaps.push('');
      } else collectGaps(k, acc);
    }
  }

  // A link row = links with nothing but separators between them. Also a row
  // of >= 2 collection links where the editor left a stray word outside an
  // anchor (live EU fr: '<a>Accessoires pour porte-</a> plaques <a>...</a>'):
  // no sentence punctuation, each gap at most 2 short words, and at most one
  // stray word per two collection links. Prose ('Shop <a>pouches</a> and
  // <a>packs</a>.') stays visible.
  function isLinkRow(el) {
    if (!el || !el.getElementsByTagName('a').length) return false;
    var outside = textOutsideLinks(el);
    if (SEPARATORS_ONLY.test(outside)) return true;
    if (SENTENCE_PUNCT.test(outside)) return false;
    var acc = { gaps: [''], coll: 0 };
    collectGaps(el, acc);
    if (acc.coll < 2) return false;
    var words = 0;
    for (var i = 0; i < acc.gaps.length; i++) {
      var g = acc.gaps[i].replace(SEPARATOR_RUN, ' ').replace(/^\s+|\s+$/g, '');
      if (!g) continue;
      if (g.length > 15) return false;
      var w = g.split(' ').length;
      if (w > 2) return false;
      words += w;
    }
    return words * 2 <= acc.coll;
  }
  function linkRowBlocks(desc) {
    var kids = elementChildren(desc);
    var blocks = [];
    for (var i = 0; i < kids.length; i++) if (BLOCK_TAGS[kids[i].tagName]) blocks.push(kids[i]);
    if (!blocks.length) return isLinkRow(desc) ? [desc] : [];
    var out = [];
    for (i = 0; i < blocks.length; i++) if (isLinkRow(blocks[i])) out.push(blocks[i]);
    return out;
  }
  function hideLinkRows() {
    var descs = document.querySelectorAll('.collection-description');
    for (var i = 0; i < descs.length; i++) {
      var blocks = linkRowBlocks(descs[i]);
      for (var j = 0; j < blocks.length; j++) {
        if (hasCls(blocks[j], CLS_LINKROW)) continue;
        setCls(blocks[j], CLS_LINKROW, true);
        S.hiddenLinkRows.push(blocks[j]);
      }
    }
  }
  function restoreLinkRows() {
    for (var i = 0; i < S.hiddenLinkRows.length; i++) setCls(S.hiddenLinkRows[i], CLS_LINKROW, false);
    S.hiddenLinkRows = [];
  }

  // ===== Row UI ==============================================================

  function checkboxLabel(cls, entry) {
    var lab = make('label', cls);
    lab.setAttribute('data-key', entry.key);
    var input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'agb-cf__input';
    input.value = entry.key;
    input.name = 'agb-cf-' + HANDLE;
    lab.appendChild(input);
    var text = make('span', 'agb-cf__text');
    paintLabel(text, '{label}', { label: entry.label });
    lab.appendChild(text);
    var count = make('span', 'agb-cf__count');
    count.hidden = true;
    lab.appendChild(count);
    input.addEventListener('change', guard('change', onInputChange));
    return { label: lab, input: input, count: count, entry: entry };
  }

  // Fill `el` from a template ('{label}: {option}'): the template's own words
  // as text, each label in its own <bdi>. Isolated, a label that starts or
  // ends with punctuation ('.308') keeps its order on a right-to-left page
  // (a plain span shows it as '308.').
  function paintLabel(el, tpl, vals) {
    while (el.firstChild) el.removeChild(el.firstChild);
    var re = /\{(\w+)\}/g, last = 0, m;
    tpl = String(tpl);
    while ((m = re.exec(tpl))) {
      if (m.index > last) el.appendChild(document.createTextNode(tpl.slice(last, m.index)));
      var v = Object.prototype.hasOwnProperty.call(vals, m[1]) ? String(vals[m[1]]) : m[0];
      el.appendChild(m[1] === 'n' ? document.createTextNode(v) : make('bdi', '', v));
      last = re.lastIndex;
    }
    if (last < tpl.length) el.appendChild(document.createTextNode(tpl.slice(last)));
  }

  function buildRow(row) {
    var fs = make('fieldset', 'agb-cf');
    fs.setAttribute('data-agb-cf', HANDLE);
    var legend = make('legend', 'agb-cf-vh', str('legend'));
    fs.appendChild(legend);
    var bar = make('div', 'agb-cf__bar');
    var chips = make('div', 'agb-cf__chips');
    var items = {};
    var dds = {};
    var ui = { root: fs, chips: chips, items: items, dds: dds, clear: null, status: null, empty: null };
    for (var i = 0; i < row.tags.length; i++) {
      var t = row.tags[i];
      if (t.kind === 'dropdown') {
        dds[t.key] = buildDropdown(t, i, ui);
        chips.appendChild(dds[t.key].btn);
      } else {
        items[t.key] = checkboxLabel('agb-cf__chip', t);
        chips.appendChild(items[t.key].label);
      }
    }
    bar.appendChild(chips);
    // No row-level "Clear" button (Dorian 2026-10-07): unticking a box clears it.
    fs.appendChild(bar);
    var status = make('span', 'agb-cf-vh');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    fs.appendChild(status);
    ui.status = status;
    // First touch / focus on the row = the shopper means it: load the rest now.
    var early = guard('engage', function () { ensureExtras('engage'); });
    fs.addEventListener('pointerdown', early);
    fs.addEventListener('focusin', early);
    // The strip scrolling (mobile) moves the button away from its popover:
    // close. A scroll right after our own repaint is the browser clamping
    // scrollLeft because the strip got narrower (shorter button text, Clear
    // hidden, a zero-match chip removed), not the shopper: keep the popover
    // and follow the button.
    chips.addEventListener('scroll', guard('strip-scroll', function () {
      var dd = S.openDd && S.ui ? S.ui.dds[S.openDd.key] : null;
      if (dd && new Date().getTime() - S.repaintAt < CLAMP_SCROLL_MS) { placePopover(dd); return; }
      closeDropdown(false);
    }));
    return ui;
  }

  // A dropdown = a chip-styled button in the strip + a popover appended to
  // <body> (outside the strip, so overflow-x never clips it).
  function buildDropdown(item, index, ui) {
    var popId = 'agb-cf-pop-' + index;
    var btn = make('button', 'agb-cf__chip agb-cf__dd');
    btn.type = 'button';
    btn.setAttribute('data-key', item.key);
    btn.setAttribute('aria-haspopup', 'true');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', popId);
    var text = make('span', 'agb-cf__text');
    paintLabel(text, '{label}', { label: item.label });
    btn.appendChild(text);
    var caret = make('span', 'agb-cf__caret', '▾');
    caret.setAttribute('aria-hidden', 'true');
    btn.appendChild(caret);

    var pop = make('div', 'agb-cf-pop');
    pop.id = popId;
    pop.setAttribute('role', 'group');
    pop.setAttribute('aria-label', item.label);
    pop.setAttribute('data-agb-cf-pop', item.key);
    pop.hidden = true;
    var listEl = make('div', 'agb-cf-pop__list');
    for (var j = 0; j < item.options.length; j++) {
      var opt = item.options[j];
      ui.items[opt.key] = checkboxLabel('agb-cf-pop__opt', opt);
      listEl.appendChild(ui.items[opt.key].label);
    }
    pop.appendChild(listEl);
    var clear = make('button', 'agb-cf__clear agb-cf-pop__clear', str('clear'));
    clear.type = 'button';
    clear.hidden = true;
    pop.appendChild(clear);

    var dd = { item: item, btn: btn, text: text, pop: pop, clear: clear };
    btn.addEventListener('click', guard('dd-click', function () {
      if (S.openDd === item) closeDropdown(false); else openDropdown(item);
    }));
    btn.addEventListener('keydown', guard('dd-key', function (e) {
      var k = keyName(e);
      if (k === 'ArrowDown') {
        if (S.openDd !== item) openDropdown(item);
        var f = popFocusables(dd);
        if (f.length) { if (e.preventDefault) e.preventDefault(); focusEl(f[0]); }
      } else if (k === 'Tab' && !e.shiftKey && S.openDd === item) {
        // Tab from the open dropdown's button goes INTO its options.
        var g = popFocusables(dd);
        if (g.length) { if (e.preventDefault) e.preventDefault(); focusEl(g[0]); }
      } else if (k === 'Escape' && S.openDd === item) {
        if (e.preventDefault) e.preventDefault();
        closeDropdown(true);
      }
    }));
    pop.addEventListener('keydown', guard('pop-key', function (e) {
      var k = keyName(e);
      if (k === 'Escape') {
        if (e.preventDefault) e.preventDefault();
        if (e.stopPropagation) e.stopPropagation();
        closeDropdown(true);
        return;
      }
      if (k !== 'Tab') return;
      var f = popFocusables(dd);
      if (!f.length) return;
      var at = f.indexOf(activeEl());
      if (e.shiftKey && at <= 0) {
        if (e.preventDefault) e.preventDefault();
        focusEl(btn);
      } else if (!e.shiftKey && at === f.length - 1) {
        // Past the last option: back into the row, after this button.
        if (e.preventDefault) e.preventDefault();
        var next = rowFocusableAfter(btn);
        closeDropdown(false);
        focusEl(next || btn);
      }
    }));
    pop.addEventListener('pointerdown', guard('engage-pop', function () { ensureExtras('engage'); }));
    clear.addEventListener('click', guard('dd-clear', function () {
      var keep = [];
      for (var i = 0; i < S.selected.length; i++) {
        var en = entryFor(S.selected[i]);
        if (!en || en.group !== item.key) keep.push(S.selected[i]);
      }
      setSelected(keep, true);
      var f = popFocusables(dd);
      if (f.length && activeEl() === clear) focusEl(f[0]);
    }));
    return dd;
  }

  function popFocusables(dd) {
    var out = [];
    for (var i = 0; i < dd.item.options.length; i++) {
      var o = dd.item.options[i];
      var it = S.ui && S.ui.items[o.key];
      if (it && !o.gone && it.label.parentNode) out.push(it.input);
    }
    if (!dd.clear.hidden) out.push(dd.clear);
    return out;
  }
  function rowFocusableAfter(btn) {
    if (!S.ui) return null;
    var list = [];
    var kids = elementChildren(S.ui.chips);
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.tagName === 'BUTTON') list.push(k);
      else { var inp = k.querySelector('input'); if (inp) list.push(inp); }
    }
    if (S.ui.clear && !S.ui.clear.hidden) list.push(S.ui.clear);
    var at = list.indexOf(btn);
    return at !== -1 && at + 1 < list.length ? list[at + 1] : null;
  }

  // ----- popover open / close / position -----

  var docListeners = null;

  function openDropdown(item) {
    if (!S.ui || !S.ui.dds[item.key] || item.gone) return;
    if (S.openDd && S.openDd !== item) closeDropdown(false);
    var dd = S.ui.dds[item.key];
    if (!dd.pop.parentNode) document.body.appendChild(dd.pop);
    paintPopover(dd);
    dd.pop.hidden = false;
    dd.btn.setAttribute('aria-expanded', 'true');
    S.openDd = item;
    placePopover(dd);
    addDocListeners();
    ensureExtras('open');
  }

  function closeDropdown(refocus) {
    var item = S.openDd;
    S.openDd = null;
    removeDocListeners();
    if (!item || !S.ui || !S.ui.dds[item.key]) return;
    var dd = S.ui.dds[item.key];
    var hadFocus = dd.pop.contains(activeEl());
    dd.pop.hidden = true;
    dd.btn.setAttribute('aria-expanded', 'false');
    if (refocus || hadFocus) focusEl(dd.btn);
  }

  function addDocListeners() {
    if (docListeners) return;
    docListeners = {
      down: guard('outside', function (e) {
        var dd = S.openDd && S.ui ? S.ui.dds[S.openDd.key] : null;
        if (!dd) return;
        var t = e.target;
        if (t && (dd.pop.contains(t) || dd.btn.contains(t))) return;
        closeDropdown(false);
      }),
      key: guard('doc-key', function (e) {
        if (S.openDd && keyName(e) === 'Escape') closeDropdown(true);
      }),
      move: guard('reposition', function () {
        var dd = S.openDd && S.ui ? S.ui.dds[S.openDd.key] : null;
        if (dd) placePopover(dd);
      })
    };
    try {
      document.addEventListener('pointerdown', docListeners.down, true);
      document.addEventListener('keydown', docListeners.key, false);
      window.addEventListener('resize', docListeners.move, false);
      window.addEventListener('scroll', docListeners.move, true);
    } catch (e) {}
  }
  function removeDocListeners() {
    if (!docListeners) return;
    try {
      document.removeEventListener('pointerdown', docListeners.down, true);
      document.removeEventListener('keydown', docListeners.key, false);
      window.removeEventListener('resize', docListeners.move, false);
      window.removeEventListener('scroll', docListeners.move, true);
    } catch (e) {}
    docListeners = null;
  }

  function isRtl(el) {
    try {
      if (typeof window.getComputedStyle === 'function') {
        var d = window.getComputedStyle(el).direction;
        if (d === 'rtl' || d === 'ltr') return d === 'rtl';
      }
    } catch (e) {}
    var n = closestMatch(el, function (x) { return !!x.getAttribute('dir'); });
    return !!n && String(n.getAttribute('dir')).toLowerCase() === 'rtl';
  }

  // The popover sits in <body>, so it does not inherit the banner's colour
  // scheme: copy the row's text colour, the first opaque background behind
  // it and its font.
  function paintPopover(dd) {
    try {
      if (typeof window.getComputedStyle !== 'function' || !S.ui) return;
      var cs = window.getComputedStyle(S.ui.root);
      var bg = '';
      for (var n = S.ui.root; n && n.nodeType === 1; n = n.parentNode) {
        var b = window.getComputedStyle(n).backgroundColor;
        if (b && b !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(b)) { bg = b; break; }
      }
      if (cs.color) dd.pop.style.color = cs.color;
      if (bg) dd.pop.style.backgroundColor = bg;
      if (cs.fontFamily) dd.pop.style.fontFamily = cs.fontFamily;
      dd.pop.setAttribute('dir', isRtl(S.ui.root) ? 'rtl' : 'ltr');
    } catch (e) {}
  }

  // Fixed position under the button (above it when there is no room below),
  // inline-start aligned with the button (RTL: its right edge), clamped to
  // the viewport with a 16 px gutter.
  function placePopover(dd) {
    var btn = dd.btn, pop = dd.pop;
    if (!inDoc(btn)) { closeDropdown(false); return; }
    var de = document.documentElement || {};
    var vw = window.innerWidth || de.clientWidth || 0;
    var vh = window.innerHeight || de.clientHeight || 0;
    var r = btn.getBoundingClientRect();
    if (vh && (r.bottom < 0 || r.top > vh)) { closeDropdown(false); return; }   // scrolled away
    var maxW = Math.max(0, vw - 2 * POP_GUTTER);
    var pr = pop.getBoundingClientRect();
    var pw = Math.min(pop.offsetWidth || pr.width || 0, maxW || Infinity);
    var ph = pop.offsetHeight || pr.height || 0;
    var left = isRtl(btn) ? r.right - pw : r.left;
    if (vw) {
      if (left + pw > vw - POP_GUTTER) left = vw - POP_GUTTER - pw;
      if (left < POP_GUTTER) left = POP_GUTTER;
    }
    var top = r.bottom + POP_GAP;
    if (vh && top + ph > vh - POP_GUTTER && r.top - POP_GAP - ph >= POP_GUTTER) top = r.top - POP_GAP - ph;
    pop.style.left = Math.round(left) + 'px';
    pop.style.top = Math.round(top) + 'px';
    if (vh) pop.style.maxHeight = Math.max(120, Math.round(vh - Math.max(top, POP_GUTTER) - POP_GUTTER)) + 'px';
  }

  function mountRow(root) {
    var banner = document.querySelector('.shopify-section--collection-banner');
    var desc = banner ? banner.querySelector('.collection-description') : null;
    if (!desc) desc = document.querySelector('.collection-description');
    if (desc && desc.parentNode) {
      desc.parentNode.insertBefore(root, desc.nextSibling);
      return 'after-description';
    }
    var content = banner ? (banner.querySelector('.prose') || banner.querySelector('.container')) : null;
    if (content) { content.appendChild(root); return 'banner-content'; }
    if (S.section && S.section.parentNode) {
      S.section.parentNode.insertBefore(root, S.section);
      return 'before-main';
    }
    return '';
  }

  function onInputChange() {
    var keys = [];
    var ents = liveEntries();
    for (var i = 0; i < ents.length; i++) {
      var it = S.ui.items[ents[i].key];
      if (it && it.input.checked && it.label.parentNode) keys.push(ents[i].key);
    }
    setSelected(keys, true);
  }

  function setSelected(keys, fromShopper) {
    S.selected = keys.slice();
    if (S.ui) {
      for (var k in S.ui.items) {
        if (!Object.prototype.hasOwnProperty.call(S.ui.items, k)) continue;
        var on = S.selected.indexOf(k) !== -1;
        S.ui.items[k].input.checked = on;
        setCls(S.ui.items[k].label, 'is-checked', on);
      }
      for (var d in S.ui.dds) {
        if (Object.prototype.hasOwnProperty.call(S.ui.dds, d)) paintDropdown(S.ui.dds[d]);
      }
      if (S.ui.clear) S.ui.clear.hidden = !S.selected.length;
    }
    if (S.selected.length) ensureExtras('select');
    apply();
    if (S.ui) S.repaintAt = new Date().getTime();
    if (fromShopper) {
      writeUrl();
      queueCae();
    }
  }

  // 'Caliber' / 'Caliber: 5.56' / 'Caliber (2)'.
  function paintDropdown(dd) {
    var on = [];
    for (var i = 0; i < dd.item.options.length; i++) {
      if (S.selected.indexOf(dd.item.options[i].key) !== -1) on.push(dd.item.options[i]);
    }
    var tpl = !on.length ? '{label}' : on.length === 1 ? str('ddOne') : str('ddMany');
    var vals = { label: dd.item.label, option: on.length ? on[0].label : '', n: on.length };
    if (dd.text.textContent !== fill(tpl, vals)) paintLabel(dd.text, tpl, vals);
    setCls(dd.btn, 'is-checked', on.length > 0);
    dd.clear.hidden = !on.length;
  }

  // ===== URL state ===========================================================

  function readUrlKeys() {
    var m = new RegExp('[?&]' + PARAM + '=([^&#]*)').exec(location.search || '');
    if (!m) return [];
    var raw;
    try { raw = decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { raw = m[1]; }
    var parts = raw.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].replace(/^\s+|\s+$/g, '').toLowerCase();
      if (p && out.indexOf(p) === -1) out.push(p);
    }
    return out;
  }
  function writeUrl() {
    try {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      var search = (location.search || '').replace(/^\?/, '');
      var params = search ? search.split('&') : [];
      var kept = [];
      for (var i = 0; i < params.length; i++) {
        if (!params[i]) continue;
        if (params[i] === PARAM || params[i].indexOf(PARAM + '=') === 0) continue;
        kept.push(params[i]);
      }
      if (S.selected.length) {
        var enc = [];
        for (var j = 0; j < S.selected.length; j++) enc.push(encodeURIComponent(S.selected[j]));
        kept.push(PARAM + '=' + enc.join(','));
      }
      var url = location.pathname + (kept.length ? '?' + kept.join('&') : '') + (location.hash || '');
      window.history.replaceState(window.history.state, '', url);
    } catch (e) { ereReport(e, 'url'); }
  }

  // ===== Analytics ===========================================================

  function queueCae() {
    try {
      if (S.caeTimer) clearTimeout(S.caeTimer);
      S.caeTimer = setTimeout(guard('cae', function () {
        S.caeTimer = 0;
        if (window.CAE_DISABLED) return;
        if (!window.CAEDev || typeof window.CAEDev.emit !== 'function') return;
        window.CAEDev.emit('collection_filter', {
          collection: caeCollection(),
          selected: S.selected.slice(),
          shown: S.lastShown
        });
      }), CAE_DEBOUNCE_MS);
    } catch (e) {}
  }

  // The collection as the ingest expects it: an object (see the header note).
  function caeCollection() {
    var id = null;
    try { var n = parseInt(window.__currentCollectionId, 10); if (!isNaN(n)) id = n; } catch (e) {}
    return { handle: HANDLE, id: id };
  }

  // Once per page view: the row is on the page with these filters.
  function caeShown() {
    try {
      if (S.shownSent || !S.row) return;
      if (window.CAE_DISABLED) return;
      if (!window.CAEDev || typeof window.CAEDev.emit !== 'function') return;
      S.shownSent = true;
      var keys = [];
      for (var i = 0; i < S.row.tags.length; i++) keys.push(S.row.tags[i].key);
      window.CAEDev.emit('collection_filter_shown', { collection: caeCollection(), filters: keys });
    } catch (e) {}
  }

  // ===== Other pages (whole-collection filtering) ============================

  function ensureExtras(why) {
    if (!S.row || S.pages <= 1) return;
    if (S.extrasState === 'loading' || S.extrasState === 'done') return;
    if (S.extrasState === 'failed') {
      if (S.loadRetried) return;
      S.loadRetried = true;
    }
    if (S.pages > MAX_PAGES) { S.extrasState = 'failed'; _dbg('too many pages to load (' + S.pages + ')'); return; }
    S.extrasState = 'loading';
    _dbg('loading the other pages (' + why + ')', S.pages);
    var wanted = [];
    for (var p = 1; p <= S.pages; p++) if (p !== S.current && !S.byPage[p]) wanted.push(p);
    var jobs = [];
    for (var i = 0; i < wanted.length; i++) jobs.push(fetchPage(wanted[i]));
    Promise.all(jobs).then(guard('pages', function (results) {
      var ok = true;
      for (var r = 0; r < results.length; r++) {
        if (!results[r] || !results[r].units) { ok = false; continue; }
        S.byPage[results[r].page] = results[r].units;
      }
      S.extrasState = ok ? 'done' : 'failed';
      S.fullKnown = ok;
      refreshRow();   // counts + zero-match removal from the detached units
      apply();        // attaches only the units that match the selection
    }))['catch'](function (err) {
      S.extrasState = 'failed';
      ereReport(err, 'pages');
      try { apply(); } catch (e) {}
    });
  }

  function preservedParams() {
    var search = (location.search || '').replace(/^\?/, '');
    var parts = search ? search.split('&') : [];
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      if (/^(sort_by|q|filter\.[^=]*)=/.test(parts[i])) out.push(parts[i]);
    }
    return out.length ? '&' + out.join('&') : '';
  }

  function fetchPage(n) {
    var url = location.pathname + '?page=' + n + '&section_id=' + encodeURIComponent(S.sectionId) + preservedParams();
    return new Promise(function (resolve) {
      var done = false;
      var to = setTimeout(function () { if (!done) { done = true; resolve(null); } }, PAGE_TIMEOUT_MS);
      var finish = function (v) { if (done) return; done = true; clearTimeout(to); resolve(v); };
      try {
        fetch(url, { credentials: 'same-origin' }).then(function (res) {
          if (!res.ok) throw new Error('page ' + n + ' HTTP ' + res.status);
          return res.text();
        }).then(function (html) {
          finish({ page: n, units: parseUnits(html) });
        })['catch'](function (err) { ereReport(err, 'page-' + n); finish(null); });
      } catch (err) { ereReport(err, 'page-' + n); finish(null); }
    });
  }

  function parseUnits(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var list = doc.querySelector('product-list');
    if (!list) return [];
    var src = unitsOf(list);
    var out = [];
    for (var i = 0; i < src.length; i++) {
      var u = document.importNode(src[i], true);
      prepareExtra(u);
      out.push(u);
    }
    return out;
  }

  // Make a fetched unit look and behave like a native card. It stays detached
  // until it matches the selection (syncExtras).
  function prepareExtra(u) {
    u[OURS] = true;
    setCls(u, CLS_EXTRA, true);
    var nodes = [u];
    var inner = u.querySelectorAll('[reveal-on-scroll]');
    for (var i = 0; i < inner.length; i++) nodes.push(inner[i]);
    var cards = u.querySelectorAll('product-card');
    for (i = 0; i < cards.length; i++) nodes.push(cards[i]);
    for (i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.removeAttribute) n.removeAttribute('reveal-on-scroll');
      if (n.style) {
        try { n.style.removeProperty('opacity'); n.style.removeProperty('transform'); n.style.removeProperty('visibility'); } catch (e) {}
      }
    }
    // Parser-made scripts never run (and stay inert in the unit). Run a fresh
    // copy of each inline script NOW, outside the grid (in <head>, removed at
    // once), so window.__shopifyCatalog knows these products while the unit
    // itself stays detached.
    var scripts = u.querySelectorAll('script');
    var host = document.head || document.documentElement;
    for (i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      if (old.getAttribute('src')) continue;
      var type = (old.getAttribute('type') || '').toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') continue;
      var fresh = document.createElement('script');
      if (type) fresh.setAttribute('type', type);
      fresh.text = old.text || old.textContent || '';
      try { host.appendChild(fresh); } catch (e) { ereReport(e, 'extra-script'); }
      try { if (fresh.parentNode) fresh.parentNode.removeChild(fresh); } catch (e) {}
    }
  }

  function otherPages() {
    var pages = [];
    for (var p in S.byPage) {
      if (!Object.prototype.hasOwnProperty.call(S.byPage, p)) continue;
      var n = parseInt(p, 10);
      if (n !== S.current) pages.push(n);
    }
    pages.sort(function (a, b) { return a - b; });
    return pages;
  }

  // Keep product-list holding exactly: the native units + those of OUR units
  // (other pages) that match the selection. Nothing selected -> no extra in
  // the grid at all. Units that should be there and are missing go in
  // collection order around the native page (earlier pages before it, later
  // pages after it); units already in place are never moved, so this never
  // fights collection-reorder.js over order.
  function syncExtras(groups) {
    var list = S.list;
    if (!list) return;
    var any = S.selected.length > 0;
    var natives = {}, standIn = {}, drop = [];
    var cur = unitsOf(list);
    for (var g = 0; g < cur.length; g++) {
      var c = cur[g];
      if (isOurs(c)) continue;
      var ch = unitHandle(c);
      if (hasCls(c, CLS_EXTRA)) {
        // A copy of one of ours another script rebuilt: it stands in for ours
        // while filtering, and goes with the filter.
        if (!any || !ch || standIn[ch]) drop.push(c); else standIn[ch] = true;
        continue;
      }
      if (ch) natives[ch] = true;
    }
    var before = [], after = [], seen = {};
    var pages = otherPages();
    for (var i = 0; i < pages.length; i++) {
      var units = S.byPage[pages[i]] || [];
      for (var j = 0; j < units.length; j++) {
        var u = units[j];
        u[OURS] = true;   // a former native page after a section swap
        var h = unitHandle(u);
        var want = any && passes(groups, h) && !natives[h] && !standIn[h] && !seen[h];
        if (h) seen[h] = true;
        if (want) { if (u.parentNode !== list) (pages[i] < S.current ? before : after).push(u); }
        else if (u.parentNode === list) drop.push(u);
      }
    }
    if (!before.length && !after.length && !drop.length) return;
    pause(function () {
      for (var d = 0; d < drop.length; d++) if (drop[d].parentNode === list) list.removeChild(drop[d]);
      var first = null;
      var kids = unitsOf(list);
      for (var k = 0; k < kids.length; k++) if (!isExtraNode(kids[k])) { first = kids[k]; break; }
      for (var b = 0; b < before.length; b++) {
        setCls(before[b], CLS_EXTRA, true);
        setCls(before[b], CLS_HIDE, false);
        list.insertBefore(before[b], first);
      }
      for (var a = 0; a < after.length; a++) {
        setCls(after[a], CLS_EXTRA, true);
        setCls(after[a], CLS_HIDE, false);
        list.appendChild(after[a]);
      }
    });
    if (before.length || after.length) decorateExtras();
  }

  function decorateExtras() {
    try { if (typeof window.__customLabelsReapply === 'function') window.__customLabelsReapply(); } catch (e) { ereReport(e, 'custom-labels'); }
    try { if (typeof window.__acoLabellerReapply === 'function') window.__acoLabellerReapply(); } catch (e) { ereReport(e, 'aco-labeller'); }
  }

  // ===== Filtering ===========================================================

  function allUnits() {
    return S.list ? unitsOf(S.list) : [];
  }

  // Native units in the grid + the (mostly detached) units of the other pages.
  function countsByKey() {
    var counts = {};
    var seen = {};
    var ents = S.row.entries;
    var t;
    for (t = 0; t < ents.length; t++) counts[ents[t].key] = 0;
    function count(u) {
      var h = unitHandle(u);
      if (!h || seen[h]) return;
      seen[h] = true;
      for (var q = 0; q < ents.length; q++) if (ents[q].members[h]) counts[ents[q].key]++;
    }
    var natives = S.list ? nativeUnits(S.list) : [];
    for (var i = 0; i < natives.length; i++) count(natives[i]);
    var pages = otherPages();
    for (var p = 0; p < pages.length; p++) {
      var units = S.byPage[pages[p]] || [];
      for (var j = 0; j < units.length; j++) count(units[j]);
    }
    return counts;
  }

  function setCount(it, n) {
    if (!it) return;
    if (S.row.showCounts) { it.count.textContent = ' (' + n + ')'; it.count.hidden = false; }
  }

  // Counts + zero-match removal, once the full set is known: a checkbox or
  // an option with no product here goes; a dropdown left with no option goes.
  function refreshRow() {
    if (!S.ui || !S.row) return;
    var known = S.fullKnown || S.pages <= 1;
    if (!known) return;
    var counts = countsByKey();
    var kept = 0;
    for (var i = 0; i < S.row.tags.length; i++) {
      var item = S.row.tags[i];
      if (item.gone) continue;
      if (item.kind === 'dropdown') {
        var dd = S.ui.dds[item.key];
        var left = 0;
        for (var j = 0; j < item.options.length; j++) {
          var o = item.options[j];
          var oi = S.ui.items[o.key];
          if (o.gone) continue;
          if (!counts[o.key]) { o.gone = true; if (oi) detach(oi.label); continue; }
          left++;
          setCount(oi, counts[o.key]);
        }
        if (!left) {
          item.gone = true;
          if (S.openDd === item) closeDropdown(false);
          if (dd) { detach(dd.btn); detach(dd.pop); }
          continue;
        }
        kept++;
      } else {
        var it = S.ui.items[item.key];
        if (!counts[item.key]) { item.gone = true; if (it) detach(it.label); continue; }
        kept++;
        setCount(it, counts[item.key]);
      }
    }
    if (!kept) { _dbg('no filter matches a product of this collection - row withdrawn'); teardown(); return; }
    S.repaintAt = new Date().getTime();   // chips / options removed, counts added
    var sel = [];
    var ents = liveEntries();
    for (i = 0; i < ents.length; i++) if (S.selected.indexOf(ents[i].key) !== -1) sel.push(ents[i].key);
    if (sel.length !== S.selected.length) {
      S.selected = sel;
      if (S.ui.clear) S.ui.clear.hidden = !sel.length;
      for (var d in S.ui.dds) {
        if (Object.prototype.hasOwnProperty.call(S.ui.dds, d)) paintDropdown(S.ui.dds[d]);
      }
      writeUrl();
    }
  }

  // The selection as groups of member sets: one group for all the checkbox
  // filters of the row, one per dropdown. Read at call time (sets may be
  // swapped by a new config).
  function selectionGroups() {
    var byGroup = {};
    var order = [];
    var ents = S.row ? S.row.entries : [];
    for (var i = 0; i < ents.length; i++) {
      var e = ents[i];
      if (S.selected.indexOf(e.key) === -1) continue;
      if (!Object.prototype.hasOwnProperty.call(byGroup, e.group)) { byGroup[e.group] = []; order.push(e.group); }
      byGroup[e.group].push(e.members);
    }
    var out = [];
    for (i = 0; i < order.length; i++) out.push(byGroup[order[i]]);
    return out;
  }
  // AND across groups, OR inside a group.
  function passes(groups, h) {
    if (!h) return false;
    for (var g = 0; g < groups.length; g++) {
      var sets = groups[g];
      var inGroup = false;
      for (var s = 0; s < sets.length; s++) { if (sets[s][h]) { inGroup = true; break; } }
      if (!inGroup) return false;
    }
    return true;
  }

  function apply() {
    if (!S.row || !S.list) return;
    var any = S.selected.length > 0;
    var groups = any ? selectionGroups() : [];
    syncExtras(groups);
    var units = allUnits();
    var shown = 0;
    for (var i = 0; i < units.length; i++) {
      var u = units[i];
      var extra = isExtraNode(u);
      var show;
      if (any) show = passes(groups, unitHandle(u));
      else show = !extra;
      setCls(u, CLS_HIDE, !show);
      if (show) shown++;
    }
    var navs = paginationNavs(S.section);
    for (var n = 0; n < navs.length; n++) setCls(navs[n], CLS_HIDE, any);
    S.lastShown = shown;
    updateStatus(any, shown);
    updateEmpty(any && shown === 0 && S.extrasState !== 'loading');
  }

  function updateStatus(any, shown) {
    if (!S.ui) return;
    var txt = any ? (shown === 1 ? str('showing1') : str('showing', shown)) : '';
    if (S.ui.status.textContent !== txt) S.ui.status.textContent = txt;
  }

  // The empty state lives OUTSIDE product-list: the old ROW reorderers wipe
  // product-list innerHTML.
  function updateEmpty(on) {
    if (!S.ui) return;
    var e = S.ui.empty;
    if (!on) {
      if (e && e.parentNode) e.parentNode.removeChild(e);
      return;
    }
    if (!e) {
      e = make('div', 'agb-cf-empty');
      e.appendChild(make('p', null, str('none')));
      var b = make('button', 'agb-cf__clear', str('clear'));
      b.type = 'button';
      b.addEventListener('click', guard('clear-empty', function () { closeDropdown(false); setSelected([], true); }));
      e.appendChild(b);
      S.ui.empty = e;
    }
    if (S.list && S.list.parentNode && (e.parentNode !== S.list.parentNode || e.nextSibling !== S.list)) {
      pause(function () { S.list.parentNode.insertBefore(e, S.list); });
    }
  }

  // ===== Resilience: observe the main section =================================

  function pause(fn) {
    var ob = S.observer;
    if (ob) { try { ob.disconnect(); } catch (e) {} }
    try { fn(); } finally {
      if (ob && S.observing && inDoc(S.observing)) {
        try { ob.observe(S.observing, { childList: true, subtree: true }); } catch (e) {}
      }
    }
  }

  function observe() {
    if (typeof MutationObserver !== 'function') return;
    var target = S.section || (S.list && S.list.parentNode);
    if (!target) return;
    if (!S.observer) {
      S.observer = new MutationObserver(guard('observer', function (records) {
        var relevant = false;
        for (var i = 0; i < records.length && !relevant; i++) {
          var t = records[i].target;
          if (t === S.list || t === S.section || !S.list || !inDoc(S.list) || (t.contains && t.contains(S.list))) relevant = true;
        }
        if (relevant) scheduleReconcile();
      }));
    }
    if (S.observing === target) return;
    try { S.observer.disconnect(); } catch (e) {}
    S.observing = target;
    S.observer.observe(target, { childList: true, subtree: true });
  }

  var reconcileQueued = false;
  function scheduleReconcile() {
    if (reconcileQueued) return;
    reconcileQueued = true;
    setTimeout(guard('reconcile', function () { reconcileQueued = false; reconcile(); }), 0);
  }

  function reconcile() {
    if (!S.row) return;
    var now = Date.now();
    if (now - S.reconcileWindow > 1000) { S.reconcileWindow = now; S.reconcileBurst = 0; }
    if (++S.reconcileBurst > 25) { _dbg('reconcile storm - pausing 2 s'); setTimeout(scheduleReconcile, 2000); return; }
    var list = findList();
    if (!list) return;
    if (list !== S.list) {
      var sec = sectionOf(list);
      var newPage = pageFromUrl();
      _dbg('grid replaced - re-applying (page ' + newPage + ')');
      if (S.section === sec || !S.section) {
        // A section swap (pagination): the previous native units become
        // extras; the new native page is captured by reference.
        S.current = newPage;
      }
      S.list = list;
      S.section = sec || S.section;
      S.sectionId = sectionIdOf(S.section) || S.sectionId;
      S.byPage[S.current] = nativeUnits(list);
      var mp = maxPage(S.section);
      if (mp > S.pages) { S.pages = mp; if (S.extrasState === 'done') { S.extrasState = 'none'; S.fullKnown = false; } }
      observe();
    }
    apply();   // re-syncs the matching extras (re-attaches dropped ones)
  }

  // ===== Lifecycle ===========================================================

  function teardown(keepUrl) {
    closeDropdown(false);
    pause(function () {
      if (S.list) {
        var units = unitsOf(S.list);
        for (var i = 0; i < units.length; i++) {
          if (isExtraNode(units[i])) S.list.removeChild(units[i]);
          else setCls(units[i], CLS_HIDE, false);
        }
      }
      var navs = paginationNavs(S.section);
      for (var n = 0; n < navs.length; n++) setCls(navs[n], CLS_HIDE, false);
      if (S.ui) {
        if (S.ui.root.parentNode) S.ui.root.parentNode.removeChild(S.ui.root);
        if (S.ui.empty && S.ui.empty.parentNode) S.ui.empty.parentNode.removeChild(S.ui.empty);
      }
    });
    if (S.ui) {
      for (var d in S.ui.dds) {
        if (Object.prototype.hasOwnProperty.call(S.ui.dds, d)) detach(S.ui.dds[d].pop);
      }
    }
    if (S.observer) { try { S.observer.disconnect(); } catch (e) {} }
    S.observing = null;
    restoreLinkRows();
    var hadSel = S.selected.length > 0;
    S.ui = null;
    S.row = null;
    S.selected = [];
    for (var p in S.byPage) {
      if (!Object.prototype.hasOwnProperty.call(S.byPage, p)) continue;
      if (parseInt(p, 10) !== S.current) delete S.byPage[p];
    }
    S.extrasState = 'none';
    S.fullKnown = false;
    if (hadSel && !keepUrl) writeUrl();
  }

  // Keys of `wanted` the row can select now, in display order.
  function validKeys(wanted) {
    var out = [];
    var ents = liveEntries();
    for (var i = 0; i < ents.length; i++) if (wanted.indexOf(ents[i].key) !== -1) out.push(ents[i].key);
    return out;
  }

  function start(cfg) {
    var row = resolveRow(cfg);
    if (!row) { _dbg('no enabled row for', HANDLE); return false; }
    var list = findList();
    if (!list) { _dbg('no product-list on this page'); return false; }
    S.list = list;
    S.section = sectionOf(list);
    S.sectionId = sectionIdOf(S.section);
    S.current = pageFromUrl();
    S.pages = maxPage(S.section);
    if (!S.sectionId) S.pages = 1;   // cannot address the other pages
    S.byPage = {};
    S.byPage[S.current] = nativeUnits(list);
    S.fullKnown = S.pages <= 1;
    S.extrasState = S.pages <= 1 ? 'done' : 'none';
    S.row = row;
    S.openDd = null;
    injectStyle();
    S.ui = buildRow(row);
    var where = mountRow(S.ui.root);
    if (!where) { S.ui = null; S.row = null; _dbg('nowhere to mount the row'); return false; }
    if (row.hideLinkRow) hideLinkRows();
    observe();
    _dbg('row mounted (' + where + '), env=' + ENV + ', members from ' + cfg.membersFrom + ', page ' + S.current + '/' + S.pages, row.tags.length + ' filter(s)');

    var fromUrl = readUrlKeys();
    refreshRow();
    if (!S.row) return true;   // every filter matched nothing (single page)
    caeShown();
    var valid = validKeys(fromUrl);
    if (valid.length) {
      setSelected(valid, false);
      if (valid.length !== fromUrl.length) writeUrl();
      revealChecked();
    } else {
      apply();
      if (fromUrl.length) writeUrl();   // only unknown keys: drop the param
      idle(function () { ensureExtras('idle'); });
    }
    return true;
  }

  // Mobile strip: a chip checked from the URL may sit past the strip's edge.
  // Scroll the STRIP (never the page) so the first checked chip (or the
  // dropdown holding the first checked option) is in view. scrollLeft deltas
  // move content the same way in LTR and RTL.
  function revealChecked() {
    try {
      if (!S.ui || !S.selected.length) return;
      var strip = S.ui.chips;
      var en = entryFor(S.selected[0]);
      if (!en) return;
      var chip = en.kind === 'option'
        ? (S.ui.dds[en.group] && S.ui.dds[en.group].btn)
        : (S.ui.items[en.key] && S.ui.items[en.key].label);
      if (!chip || typeof strip.getBoundingClientRect !== 'function') return;
      if (strip.scrollWidth <= strip.clientWidth) return;
      var sr = strip.getBoundingClientRect();
      var cr = chip.getBoundingClientRect();
      var delta = 0;
      if (cr.left < sr.left) delta = cr.left - sr.left - 8;
      else if (cr.right > sr.right) delta = cr.right - sr.right + 8;
      if (delta) strip.scrollLeft += delta;
    } catch (e) {}
  }

  function idle(fn) {
    var g = guard('idle', fn);
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(g, { timeout: 2000 });
    else setTimeout(g, 1200);
  }

  // What the row is drawn from. Leaves out the re-check stamps (resolvedAt,
  // stale, membersFrom): the backend re-resolves every 30 min with a new
  // resolvedAt (and ETag) even when no product moved.
  function configKey(cfg) {
    try {
      return JSON.stringify({ enabled: cfg.enabled, tags: cfg.tags, members: cfg.members, collectionFilters: cfg.collectionFilters });
    } catch (e) { return ''; }
  }

  function useConfig(cfg) {
    var json = configKey(cfg);
    if (S.cfg && json === S.cfgJson) { S.cfg = cfg; return; }   // nothing the row shows changed: keep it (open popover, focus, loaded pages)
    var keep = S.selected.slice();
    if (S.row) teardown(true);
    S.cfg = cfg;
    S.cfgJson = json;
    if (start(cfg) && keep.length && S.row && !S.selected.length) {
      var valid = validKeys(keep);
      if (valid.length) setSelected(valid, false);
      writeUrl();
    }
  }

  // ===== Boot ================================================================

  var pendingCfg = null;

  // True once every DOMContentLoaded listener has run (navigation timing).
  function dclDone() {
    try {
      var perf = window.performance;
      if (!perf) return false;
      var nav = typeof perf.getEntriesByType === 'function' ? perf.getEntriesByType('navigation')[0] : null;
      if (nav) return nav.domContentLoadedEventEnd > 0;
      return !!(perf.timing && perf.timing.domContentLoadedEventEnd > 0);
    } catch (e) { return false; }
  }

  // DOMContentLoaded EVENT + one macrotask. A deferred script runs while
  // readyState is already 'interactive' but BEFORE DOMContentLoaded (and the
  // old reorderers' handlers), so readyState alone is not a signal. 'load'
  // is the fallback for a late injection after DOMContentLoaded.
  function whenReady(fn) {
    var fired = false;
    var run = function () {
      if (fired) return;
      fired = true;
      try { document.removeEventListener('DOMContentLoaded', run); } catch (e) {}
      try { window.removeEventListener('load', run); } catch (e) {}
      setTimeout(guard('ready', fn), 0);
    };
    if (document.readyState === 'complete' || dclDone()) { run(); return; }
    document.addEventListener('DOMContentLoaded', run);
    window.addEventListener('load', run);
  }

  var boot = guard('boot', function () {
    var cached = readCache();
    whenReady(function () {
      S.ready = true;
      if (cached) { _dbg('rendering from the cached config'); useConfig(cached); }
      if (pendingCfg) { var p = pendingCfg; pendingCfg = null; useConfig(p); }
    });
    loadConfig(cached, guard('fresh', function (cfg, same) {
      if (same && S.cfg) return;
      if (!S.ready) { pendingCfg = cfg; return; }
      useConfig(cfg);
    }));
  });

  window.__agbCollectionFilter = {
    state: S,
    reapply: guard('devtools', function () { reconcile(); }),
    open: guard('devtools', function (key) { if (S.row && S.ui && S.ui.dds[key]) openDropdown(S.ui.dds[key].item); }),
    close: guard('devtools', function () { closeDropdown(false); }),
    version: 2
  };

  boot();
})();
